<template>
  <div>
    <div class="resource_card_wrapper__with_buttons">
      <div
        class="resource_card"
        tabindex="0"
        @keyup.enter="toggleModal(true)"
      >
        <div
          v-if="screenshots.length > 0"
          role="button"
          @click="toggleModal(true)"
          :aria-label="`Open preview for ${resourceCardDetails.ariaLabel}`"
          class="resource_card_image__hover"
        >
          <div class="resource_card_title_wrapper__hover">
            <span
              v-html="resourceCardDetails.resourceIcon"
              class="resource_card_icon"
              aria-hidden="true"
            />
            <span aria-hidden="true">
              {{ resourceCardDetails.hoverTitleWithSet }}
            </span>
          </div>
        </div>
        <div
          v-if="set_number && set_number !== 0"
          class="resource_set_number"
        >
          Set {{ set_number }}
        </div>
        <div class="resource_card_title_wrapper">
          <span
            v-html="resourceCardDetails.resourceIcon"
            class="resource_card_icon"
            aria-hidden="true"
          />
          <span
            aria-hidden="true"
            class="resource_card_title ellipsis"
          >
            {{ resourceCardDetails.title }}
          </span>
        </div>
        <IKImageS3Wrapper
          :aws-url="thumbnail_url"
          alt=""
          :transformation="[{width: 150}]" />
      </div>

      <div
        class="resource_buttons__hover"
        @click.stop
        @keyup.enter.stop
      >
        <ResourceDownloadButton
          style="display: inline-block"
          :resource="{uuid, type, title, download_links, google_drive_links, language, set_number}"
          :condense-buttons=true
          @resourceDownloadClicked="$emit('resourceDownloadClicked')"
          @resourceDownloadOpened="$emit('resourceDownloadOpened')"
          :evt-data="evtData"
        />
        <button
          v-if="!savePermissions.block && showSaveButton"
          class="btn btn-success save-button"
          :aria-label="`Save ${title}`"
          v-tooltip.top.unenterable="'Save'"
          @click="toggleSaveModal(true)">
          <span class="material-icons-round pr-1">save</span>
        </button>
      </div>

      <ResourcePreviewModal
        v-if="showPreviewModal"
        @preview:close="toggleModal(false)"
        @resourcePreviewShown="$emit('resourcePreviewShown')"
        :uuid="uuid"
        :title="title"
        :type="type"
        :download-links="download_links"
        :google-drive-links="google_drive_links"
        :screenshots="screenshots"
        :num-pages="num_pages"
        :language="language"/>

      <SaveResourceModal :content-uuid="uuid" :open.sync="SaveModalShown" />
    </div>
  </div>
</template>

<script>
import ResourceDownloadButton from '../ResourceDownloadButton'
import ResourcePreviewModal from '../ResourcePreviewModal'
import SaveResourceModal from '../SaveResourceModal'
import ResourceFoundIn from '../search/ResourceFoundIn.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper'
import { pmaSvg, dtsSvg } from '@/icons'

import { mapGetters } from 'vuex'
import { tooltip } from 'uiv'

import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'

export default {
  name: 'ResourceTile',
  directives: { tooltip },
  components: {
    ResourceDownloadButton,
    ResourcePreviewModal,
    ResourceFoundIn,
    SaveResourceModal,
    IKImageS3Wrapper
  },
  mixins: [noSelMixin],
  props: {
    uuid: {
      required: true,
      type: String
    },
    title: {
      required: true,
      type: String
    },
    type: {
      required: true,
      type: String
    },
    thumbnail_url: {
      required: true,
      type: String
    },
    num_pages: {
      default: () => {
        return 0
      },
      type: Number
    },
    screenshots: {
      required: false,
      type: Array,
      default: () => {
        return []
      }
    },
    download_links: {
      required: true,
      type: Array
    },
    google_drive_links: {
      required: false,
      type: Array,
      default: () => {
        return []
      }
    },
    showSaveButton: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    alignedContent: {
      type: Object,
      required: false,
      default: () => {}
    },
    position: {
      type: Number,
      required: false,
      default: null
    },
    set_number: {
      type: Number,
      required: false,
      default: 0
    },
    evtData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      showPreviewModal: false,
      SaveModalShown: false,
      pmaSvg: pmaSvg,
      dtsSvg: dtsSvg
    }
  },
  eventContext() {
    return {
      resource_preview_type: 'card',
      resource_uuid: this.uuid,
      resource_title: this.title,
      resource_language: this.language,
      // @TODO:// What should the ref be here?
      position: this.position, ref: 'home',
      ...this.evtData
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo']),
    savePermissions() {
      return actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.RESOURCE, {}, this.authInfo)
    },
    displaySubjects() {
      return this.getResourceSubjectLabelsFromValues(this.subjects)
    },
    formattedPMATitle() {
      const titleWithoutPrefix = this.title.split(':')
      if (titleWithoutPrefix.length > 1) {
        return titleWithoutPrefix[1];
      }
      return this.title
    },
    truncatedTitle() {
      const maxLen = 43
      if (this.formattedPMATitle.length > maxLen) {
        return this.formattedPMATitle.slice(0, maxLen) + '...'
      }
      return this.formattedPMATitle
    },
    resourceCardDetails() {
      const setTitle = this.set_number && this.set_number !== 0 ? ` - Set ${this.set_number}` : ''

      if (this.type === 'Data Tracking Sheet' || this.type === 'Progress Monitoring Data Sheet') {
        return {
          resourceIcon: dtsSvg,
          ariaLabel: 'Data Tracking Sheet',
          title: 'Data Tracking Sheet',
          hoverTitleWithSet: 'Data Tracking Sheet' + setTitle,
        }
      } else {
        return {
          resourceIcon: pmaSvg,
          ariaLabel: this.formattedPMATitle,
          title: this.formattedPMATitle,
          hoverTitleWithSet: this.truncatedTitle + setTitle,
        }
      }
    },
  },
  methods: {
    toggleModal(val) {
      if (val !== undefined) {
        this.showPreviewModal = val
      } else {
        this.showPreviewModal = !this.showPreviewModal
      }
    },
    toggleSaveModal(open) {
      this.SaveModalShown = open

      if (open) {
        this.$sendEvent('Clicked Save Resource')
        this.$emit('resourceSaved')
      }
    }
  }
}
</script>

<style scoped>
.resource_card_wrapper__with_buttons{
  position: relative;
  width: 176px;
  height: 119px;
  margin: 12px 24px 18px 0;
  border: 1px solid #CDCDCD;
}
.resource_card {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: white;
}

.resource_card_title_wrapper {
  position: absolute;
  display: flex;
  align-items: center;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 32px;
  background-color: #EBEBEB;
}

.resource_card_icon {
  padding: 6px;
  color: #8C8C8C;
}

.resource_card_title {
  text-align: left;
  height: fit-content;
  font-weight: 500;
}

.resource_set_number {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #EBEBEB;
  color: #333;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
}

.resource_panel {
  padding: 20px;
  padding-left: 8px;
}
.multilingual-resources .resource_panel {
  padding-top: 10px;
}

.resource_card_image__hover {
  visibility: hidden;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 6px 4px 10px;
  background-color: #EBEBEB;
  text-align: left;
  cursor: pointer;
}

.resource_card_wrapper__with_buttons:focus-within .resource_card_title_wrapper,
.resource_card_wrapper__with_buttons:hover .resource_card_title_wrapper,
.resource_card:focus-within .resource_card_title_wrapper,
.resource_card:hover .resource_card_title_wrapper {
  visibility: hidden;
}

.resource_card_wrapper__with_buttons:focus-within .resource_set_number,
.resource_card_wrapper__with_buttons:hover .resource_set_number,
.resource_card:focus-within .resource_set_number,
.resource_card:hover .resource_set_number {
  visibility: hidden;
}

.resource_card_wrapper__with_buttons:focus-within .resource_card_image__hover,
.resource_card_wrapper__with_buttons:hover .resource_card_image__hover,
.resource_card_wrapper__with_buttons:focus-within .resource_buttons__hover,
.resource_card_wrapper__with_buttons:hover .resource_buttons__hover {
  visibility: visible;
}

.resource_card_title_wrapper__hover {
  display: flex;
}

.resource_buttons__hover {
  visibility: hidden;
  position: absolute;
  bottom: 8px;
  left: 16px;
  width: fit-content;
}

.save-button {
  background-color: var(--green-default);
  padding: 3px;
  margin-left: 3px;
  display: inline-flex;
  align-items: center;
  .material-icons-round {
    font-size: 32px;
  }
}

.save-button:hover {
  background-color: var(--green-button-hover);
}

::v-deep .btn-group {
  margin-right: 2px;
}

::v-deep .btn-gb-light:focus {
  outline: solid 2px var(--gray-default);
  outline-offset: 2px;
}
</style>
